import React from "react";
import { Link, useNavigate } from "react-router-dom";
import ValueCell from "./ValueCell";
import VsCell from "./VsCell";
import MiniDistribution from "./MiniDistribution";
import HeroIcon from "../common/HeroIcon";
import { formatAnalysisTooltip, OUTLIER_TOOLTIP } from "./utils";

const MetricValueCell = ({
  value,
  formatNumber,
  distribution,
  patternMedian,
  treatZeroAsMissing = false,
}) => {
  const showAsMissing = value == null || (treatZeroAsMissing && value === 0);
  return (
    <div className="flex items-center justify-end gap-2">
      <ValueCell value={value} formatNumber={formatNumber} treatZeroAsMissing={treatZeroAsMissing} />
      {!showAsMissing && distribution?.length ? (
        <MiniDistribution
          distribution={distribution}
          patternMedian={patternMedian}
          compact
        />
      ) : null}
    </div>
  );
};

const PatternRow = ({
  pattern,
  baseline,
  maxVideoCount = 0,
  formatNumber,
  audienceHash,
  patternType,
}) => {
  const navigate = useNavigate();
  const rawCount = pattern.stats?.video_count;
  const hasCount = typeof rawCount === "number";
  const countBarPct =
    hasCount && maxVideoCount > 0 ? Math.min(100, Math.max(0, (rawCount / maxVideoCount) * 100)) : 0;

  const overviewPath = `/audience/${audienceHash}/patterns/${encodeURIComponent(patternType)}/${encodeURIComponent(pattern.id)}/overview`;
  const scriptsPath = `/audience/${audienceHash}/patterns/${encodeURIComponent(patternType)}/${encodeURIComponent(pattern.id)}/scripts`;
  const analysisTooltip = formatAnalysisTooltip(pattern.analysis);

  return (
  <tr
    onClick={() => navigate(overviewPath)}
    className="group cursor-pointer border-b border-theme-secondary hover:bg-theme-secondary transition-colors"
  >
      <td className="px-4 py-2 align-middle text-sm font-medium text-theme-text w-[360px] max-w-[360px] overflow-hidden">
        <div className="min-w-0 flex flex-col gap-0.5">
          <span className="inline-flex max-w-full min-w-0 items-center gap-1">
            <span
              className="truncate text-sm font-medium text-theme-text min-w-0"
              title={pattern.name ?? pattern.id ?? undefined}
            >
              {pattern.name ?? pattern.id ?? "—"}
            </span>
            {analysisTooltip ? (
              <span
                className="shrink-0 cursor-help text-theme-text opacity-40 hover:opacity-70"
                data-tooltip-id="pattern-analysis-tip"
                data-tooltip-content={analysisTooltip}
                aria-label="View pattern analysis"
                onClick={(event) => event.stopPropagation()}
              >
                <HeroIcon name="information-circle" className="h-4 w-4" />
              </span>
            ) : null}
          </span>
          {pattern.subtitle != null && String(pattern.subtitle).trim() !== "" ? (
            <span
              className="text-xs text-theme-text opacity-50 truncate min-w-0 block"
              title={`"${String(pattern.subtitle).trim()}"`}
            >
              "{String(pattern.subtitle).trim()}"
            </span>
          ) : null}
        </div>
      </td>
      <td className="px-4 py-2 align-middle whitespace-nowrap text-sm text-theme-text text-right">
        <MetricValueCell
          value={pattern.stats?.likes}
          formatNumber={formatNumber}
          distribution={baseline?.likes?.distribution}
          patternMedian={pattern?.stats?.likes}
          treatZeroAsMissing
        />
      </td>
      <td className="px-4 py-2 align-middle whitespace-nowrap text-sm text-left">
        <VsCell
          value={pattern.creator_comparison?.likes != null ? 1 : null}
          ratio={pattern.creator_comparison?.likes}
          title={OUTLIER_TOOLTIP}
        />
      </td>
      <td className="px-4 py-2 align-middle whitespace-nowrap text-sm text-theme-text text-right">
        <MetricValueCell
          value={pattern.stats?.views}
          formatNumber={formatNumber}
          distribution={baseline?.views?.distribution}
          patternMedian={pattern?.stats?.views}
          treatZeroAsMissing
        />
      </td>
      <td className="px-4 py-2 align-middle whitespace-nowrap text-sm text-left">
        <VsCell
          value={pattern.creator_comparison?.views != null ? 1 : null}
          ratio={pattern.creator_comparison?.views}
          title={OUTLIER_TOOLTIP}
        />
      </td>
      <td className="px-4 py-2 align-middle whitespace-nowrap text-sm text-theme-text text-right">
        <MetricValueCell
          value={pattern.stats?.comments}
          formatNumber={formatNumber}
          distribution={baseline?.comments?.distribution}
          patternMedian={pattern?.stats?.comments}
          treatZeroAsMissing
        />
      </td>
      <td className="px-4 py-2 align-middle whitespace-nowrap text-sm text-left">
        <VsCell
          value={pattern.creator_comparison?.comments != null ? 1 : null}
          ratio={pattern.creator_comparison?.comments}
          title={OUTLIER_TOOLTIP}
        />
      </td>
      <td className="px-4 py-2 align-middle text-sm text-theme-text text-left w-40 min-w-[10rem]">
        <div className="relative flex min-h-[1.375rem] w-full min-w-0 items-center">
          <div
            className="pointer-events-none opacity-50 absolute left-0 top-1/2 h-5 max-w-full -translate-y-1/2 rounded-sm bg-theme-shadow"
            style={{ width: `${countBarPct}%` }}
            aria-hidden
          />
          <span className="relative z-[1] px-1 text-xs tabular-nums">
            {hasCount && rawCount > 0 ? rawCount : <span className="text-theme-text opacity-40">--</span>}
          </span>
        </div>
      </td>
    <td className="px-4 py-2 align-middle whitespace-nowrap text-right">
      <Link
        to={scriptsPath}
        onClick={(event) => event.stopPropagation()}
        className="inline-block px-3 py-1 rounded-lg border border-theme-secondary bg-theme-secondary text-theme-text text-sm font-semibold transition-colors hover:border-theme-primary"
      >
        Create
      </Link>
    </td>
  </tr>
  );
};

export default PatternRow;
